import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useAuthorization } from "@common/providers";
import {
  useGetProjectByIdQuery,
  useListQuotesForAccountQuery,
} from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import Show from "./Show/Show";
import { ThirdPartyService } from "./types/ThirdPartyService";

interface Props {
  project_id: string;
}

const ThirdPartyPricingV2Mspa: React.FC<Props> = ({ project_id }) => {
  const projectId = project_id;
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  const { data: project, isLoading: projectLoading } =
    useGetProjectByIdQuery({
      id: projectId as any,
      slug: accountSlug,
    });

  const { data: quotesData, isLoading: quotesLoading } =
    useListQuotesForAccountQuery({
      slug: accountSlug,
      filter: { project: projectId },
      include: ["vendor"],
      page: { size: 250 },
    });

  const authorized = useAuthorization();

  if (!authorized || projectLoading || quotesLoading || !project) {
    return <ScopeStackSpinner />;
  }

  const currencyUnit =
    (project?.data?.attributes as any)?.["field-labels"]?.currency_unit || "$";

  const vendors: Record<string, string> = {};
  if (quotesData?.included) {
    (quotesData.included as any[]).forEach((item: any) => {
      if (item.type === "vendors") {
        vendors[String(item.id)] = item.attributes?.name || "";
      }
    });
  }

  const services: ThirdPartyService[] =
    (quotesData?.data as any[])?.map((quote: any) => {
      const attr = quote.attributes;
      const vendorData = quote.relationships?.vendor?.data;
      const vendorName = vendorData ? vendors[String(vendorData.id)] || "" : "";
      const terms = parseFloat(attr["mrr-terms"]) || 0;
      const oneTimeRevenue = parseFloat(attr.price) || 0;
      const recurringRevenue =
        (parseFloat(attr["recurring-price"]) || 0) * terms;
      const totalRevenue = parseFloat(attr["total-revenue"]) || 0;
      const oneTimeCost = parseFloat(attr.cost) || 0;
      const recurringCost =
        (parseFloat(attr["recurring-cost"]) || 0) * terms;
      const profit = parseFloat(attr["profit"]) || 0;
      const margin = totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;

      return {
        id: String(quote.id),
        vendorName,
        description: attr.description || "",
        oneTimeRevenue,
        recurringRevenue,
        totalRevenue,
        oneTimeCost,
        recurringCost,
        profit,
        margin,
      };
    }) || [];

  return (
    <div className="tab-content">
      <div className="tab-pane active">
        <Show
          services={services}
          currencyUnit={currencyUnit}
          isLoading={quotesLoading}
        />
      </div>
    </div>
  );
};

export default ThirdPartyPricingV2Mspa;
